import styled from 'styled-components';
import { ArtworkResponse } from '../../services';
import { EnhancedArtworkCard } from '../gallery';
import { GalleryArtworkCardSkeleton } from '../ui';
import { ArtworkObjectImage } from './ArtworkObjectImage';

export interface ArtworkCardProps {
  artwork: ArtworkResponse;
  aspectRatio?: number;
  onArtworkClick?: (artwork: ArtworkResponse) => void;
  className?: string;
}

export function ArtworkCard({
  artwork,
  aspectRatio = 1,
  onArtworkClick,
  className,
}: ArtworkCardProps) {
  return (
    <EnhancedArtworkCard
      artwork={artwork}
      aspectRatio={aspectRatio}
      onClick={onArtworkClick}
      className={className}
    />
  );
}

export function ArtworkCardSkeleton({
  aspectRatio = 1,
}: {
  aspectRatio?: number;
}) {
  return <GalleryArtworkCardSkeleton aspectRatio={aspectRatio} />;
}

const CompactCard = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  gap: ${({ theme }) => theme.spacing.md};
  border: 0;
  background: transparent;
  color: inherit;
  padding: ${({ theme }) => theme.spacing.md};
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.semantic.focus};
    outline-offset: 3px;
  }
`;

const CompactImage = styled.div`
  flex: 0 0 auto;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.borderRadius.md};

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const CompactContent = styled.div`
  min-width: 0;
`;

const StudentName = styled.div`
  color: ${({ theme }) => theme.colors.text};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UploadDate = styled.div`
  color: ${({ theme }) => theme.colors.gray.dark};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

export interface CompactArtworkCardProps extends Omit<ArtworkCardProps, 'aspectRatio'> {
  size?: number;
}

export function CompactArtworkCard({
  size = 60,
  artwork,
  onArtworkClick,
  className,
}: CompactArtworkCardProps) {
  return (
    <CompactCard
      type="button"
      onClick={() => onArtworkClick?.(artwork)}
      className={className}
      data-testid="compact-artwork-card"
    >
      <CompactImage style={{ width: size, height: size }}>
        <ArtworkObjectImage
          artworkId={artwork.artwork_id}
          variant="thumbnail"
          updatedAt={artwork.updated_at}
          alt=""
        />
      </CompactImage>
      <CompactContent>
        <StudentName>{artwork.student_name || 'Unknown Student'}</StudentName>
        <UploadDate>{new Date(artwork.uploaded_at).toLocaleDateString()}</UploadDate>
      </CompactContent>
    </CompactCard>
  );
}
